import type { JSX } from 'react';
import { cn } from '@lib/cn';

/**
 * Heading level of the title. The Hero passes `'h1'` (page-level heading,
 * exactly one per page); every other section defaults to `'h2'` for a correct
 * document outline.
 */
export type SectionHeadingLevel = 'h1' | 'h2';

/**
 * Alignment of the entire heading block (eyebrow + title + subtitle). Hero is
 * `'left'`; the final CTA section will later pass `'center'`.
 */
export type SectionHeadingAlign = 'left' | 'center';

export interface SectionHeadingProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  as?: SectionHeadingLevel;
  align?: SectionHeadingAlign;
  className?: string;
  /** ID atribuido ao elemento de titulo. Usado por `aria-labelledby` no `<section>` pai. */
  titleId?: string;
}

/**
 * Top-level class constants — keeps magic strings out of the JSX body
 * (clean-code §1) and pins the visual contract to UI-SPEC §3 (SectionHeading
 * Visual Contract). Any edit here is intentional and audit-able.
 */
const ALIGN_CLASSES: Record<SectionHeadingAlign, string> = {
  left: 'text-left',
  center: 'text-center',
};

const TITLE_CLASSES =
  'font-display font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-tight text-neutral-800';

const SUBTITLE_CLASSES = 'font-sans font-medium text-xl text-neutral-600 leading-snug mt-6';

const EYEBROW_CLASSES = 'text-xs font-semibold uppercase tracking-wider text-brand-600 mb-3';

/**
 * Reusable section heading: optional eyebrow + heading (h1 or h2) + optional
 * subtitle. The Hero (Plan 06) consumes this with `as="h1"`; every other
 * section uses the default `<h2>`.
 *
 * @example Hero usage
 * ```tsx
 * <SectionHeading
 *   as="h1"
 *   title={landingCopy.hero.headline}
 *   subtitle={landingCopy.hero.subtitle}
 *   align="left"
 * />
 * ```
 */
export const SectionHeading = ({
  title,
  subtitle,
  eyebrow,
  as = 'h2',
  align = 'left',
  className,
  titleId,
}: SectionHeadingProps): JSX.Element => {
  const Heading = as;
  return (
    <div className={cn(ALIGN_CLASSES[align], className)}>
      {eyebrow !== undefined ? <p className={EYEBROW_CLASSES}>{eyebrow}</p> : null}
      <Heading id={titleId} className={TITLE_CLASSES}>
        {title}
      </Heading>
      {subtitle !== undefined ? <p className={SUBTITLE_CLASSES}>{subtitle}</p> : null}
    </div>
  );
};
